import { Component } from '@theme/component';

const APP_PROXY_BASE = '/apps/sp5der';

async function fetchJSON(url, init) {
  const requestUrl = new URL(url, window.location.origin);
  const method = init?.method ? String(init.method).toUpperCase() : 'GET';

  // Vote counts change while the shopper is on the PDP; bust browser/CDN
  // caches so back-navigation to the collection always renders fresh data.
  if (method === 'GET') {
    requestUrl.searchParams.set('_', Date.now().toString());
  }

  const response = await fetch(requestUrl, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    ...init,
  });

  return response.json();
}

function escapeHTML(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function getVotingContext() {
  const params = new URLSearchParams(window.location.search);
  const votingId = params.get('votingId');
  const collectionHandle = params.get('collectionHandle');

  return {
    votingId: votingId && votingId.trim() ? votingId : null,
    collectionHandle: collectionHandle && collectionHandle.trim() ? collectionHandle : null,
  };
}

function normalizeProductId(value) {
  if (value == null) return null;

  const stringValue = String(value);
  const match = stringValue.match(/(?:gid:\/\/shopify\/Product\/)?(\d+)$/);
  return match ? match[1] : stringValue;
}

function pluralize(count, oneLabel = '', otherLabel = '') {
  const label = Number(count) === 1 ? oneLabel : otherLabel;
  return `${count} ${label}`.trim();
}

class CommunityVotingController extends Component {
  #handlePageShow = (event) => {
    if (this.dataset.mode !== 'collection' || !event.persisted) return;

    void this.#initCollectionMode();
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.mode === 'collection') {
      window.addEventListener('pageshow', this.#handlePageShow);
      void this.#initCollectionMode();
      return;
    }

    if (this.dataset.mode === 'product') {
      void this.#initProductMode();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  #waitForButton() {
    const existingButton = this.querySelector('[data-voting-action-button]');
    if (existingButton instanceof HTMLButtonElement) {
      return Promise.resolve(existingButton);
    }

    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        const button = this.querySelector('[data-voting-action-button]');
        if (!(button instanceof HTMLButtonElement)) return;

        observer.disconnect();
        resolve(button);
      });

      observer.observe(this, { childList: true, subtree: true });
    });
  }

  #setLoadingState(element, isLoading) {
    if (!(element instanceof HTMLElement)) return;
    element.toggleAttribute('aria-busy', isLoading);
    element.dataset.votingLoading = isLoading ? 'true' : 'false';
  }

  #setVotingButtonLoading(button, form, isLoading) {
    this.#setLoadingState(form, isLoading);
    button.disabled = isLoading;

    if (isLoading) {
      button.dataset.loading = 'true';
      button.textContent = this.dataset.labelLoadingVote || '';
      return;
    }

    delete button.dataset.loading;
  }

  #createVoteStatusIndicator() {
    const status = document.createElement('div');
    status.className = 'community-voting-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    return status;
  }

  #setVoteStatus(status, state) {
    if (!(status instanceof HTMLElement)) return;

    status.classList.remove('community-voting-status--success', 'community-voting-status--error');

    if (!state) {
      status.hidden = true;
      status.textContent = '';
      status.removeAttribute('aria-label');
      status.removeAttribute('title');
      return;
    }

    const isSuccess = state === 'success';
    const label = isSuccess ? this.dataset.labelVoteSuccess || '' : this.dataset.labelVoteError || '';
    status.hidden = false;
    status.classList.add(isSuccess ? 'community-voting-status--success' : 'community-voting-status--error');
    status.textContent = `${isSuccess ? '✓' : '✕'} ${label}`.trim();
    status.setAttribute('aria-label', label);
    status.setAttribute('title', label);
  }

  async #initCollectionMode() {
    const { votingId, collectionHandle: votingCollectionHandle } = getVotingContext();
    const collectionHandle = votingCollectionHandle || this.dataset.collectionHandle;
    const grid = this.querySelector('[data-testid="product-grid"]');
    if (!(grid instanceof HTMLElement) || !votingId || !collectionHandle) {
      return;
    }

    const url = new URL(`${APP_PROXY_BASE}/community-votings/collection`, window.location.origin);
    url.searchParams.set('votingId', votingId);
    url.searchParams.set('collectionHandle', collectionHandle);

    try {
      const data = await fetchJSON(url);
      const voting = data?.voting;
      if (!voting) {
        return;
      }

      const products = Array.isArray(voting.products) ? voting.products : [];
      if (!products.length) {
        return;
      }

      const totalVotes = products.reduce((sum, item) => sum + Number(item.votesCount ?? 0), 0);

      grid.replaceChildren();

      for (const [index, product] of products.entries()) {
        const li = document.createElement('li');
        li.id = `${this.id || 'community-voting'}-${product.productId}`;
        li.className = `product-grid__item product-grid__item--${index}`;
        li.dataset.productId = String(product.productId);
        li.dataset.page = '1';
        li.setAttribute('ref', 'cards[]');

        li.innerHTML = this.#renderProductCard(product, voting, totalVotes);
        grid.append(li);
      }
    } catch (error) {
      console.error('community-voting collection init failed', error);
    }
  }

  #renderProductCard(product, voting, totalVotes = 0) {
    const link = product.link || (product.handle
      ? `/products/${encodeURIComponent(product.handle)}?votingId=${encodeURIComponent(voting.id)}&collectionHandle=${encodeURIComponent(voting.collectionHandle)}`
      : '#');
    const title = escapeHTML(product.title || '');
    const votesCount = Number(product.votesCount ?? 0);
    const percentage = totalVotes > 0 ? Math.round((votesCount / totalVotes) * 100) : 0;
    const selectedProductId = normalizeProductId(voting.selectedProductId ?? voting.product?.productId ?? null);
    const currentProductId = normalizeProductId(product.productId);
    const hasVoteFlag =
      product.hasVoted === true ||
      product.isSelected === true ||
      product.selected === true ||
      product.voted === true;
    const isSelected = hasVoteFlag || (selectedProductId && currentProductId && selectedProductId === currentProductId);
    const voteCountLabel = pluralize(votesCount, this.dataset.labelVoteCountOne, this.dataset.labelVoteCountOther);
    const voteTitle = isSelected ? `${this.dataset.labelYourVote || ''}, ${voteCountLabel}`.trim() : voteCountLabel;
    const overlayClass = isSelected ? 'product-card-vote-overlay__progress--selected' : 'product-card-vote-overlay__progress--idle';
    return `
      <product-card class="product-card" data-product-id="${escapeHTML(product.productId)}" id="product-card-${escapeHTML(product.id)}" data-product-transition="false" title="${escapeHTML(voteTitle)}">
        <a class="product-card__link" href="${escapeHTML(link)}" ref="productCardLink">
          <span class="visually-hidden">${title}</span>
        </a>
        <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style">
          <div ref="cardGallery" class="card-gallery spacing-style border-style" data-product-id="${escapeHTML(product.productId)}" data-hover-fade="" data-image-ratio="square">
            <a class="contents" ref="cardGalleryLink" href="${escapeHTML(link)}" aria-label="${title}">
              <div class="product-media" style="--ratio: 1.0" data-media-id="${escapeHTML(product.id)}">
                ${product.imageUrl ? `<img src="${escapeHTML(product.imageUrl)}" alt="${title}" loading="lazy" width="1600" height="1600" class="product-media__image">` : ''}
                <div class="product-card-vote-overlay" aria-hidden="true">
                  <div class="product-card-vote-overlay__background"></div>
                  <div class="product-card-vote-overlay__progress ${overlayClass}" style="height: ${percentage}%;"></div>
                  <div class="product-card-vote-overlay__label">
                    <span>${percentage}%</span>
                  </div>
                </div>
              </div>
            </a>
          </div>
          <a class="contents user-select-text" ref="productTitleLink" href="${escapeHTML(link)}">
            <div class="spacing-style text-block h5 text-block--align-left" style="--width: 100%;">
              <p>${title}</p>
            </div>
          </a>
        </div>
      </product-card>
    `;
  }

  async #initProductMode() {
    const { votingId, collectionHandle: votingCollectionHandle } = getVotingContext();
    const collectionHandle = votingCollectionHandle || this.dataset.collectionHandle;
    const productId = this.dataset.productId;
    const form = this.querySelector('product-form-component');
    if (!(form instanceof HTMLElement) || !votingId || !collectionHandle || !productId) {
      return;
    }

    // Klaviyo onsite.js loads lazily and injects its own "Notify me" trigger
    // next to sold-out forms; this attribute lets CSS keep it hidden in
    // voting mode no matter when it appears.
    this.setAttribute('data-voting-active', '');

    const button = await this.#waitForButton();
    if (!(button instanceof HTMLButtonElement) || !button.isConnected) {
      return;
    }

    const votingButton = button.cloneNode(true);
    votingButton.type = 'button';
    votingButton.name = '';
    votingButton.removeAttribute('on:click');
    votingButton.dataset.votingActionButton = 'true';
    votingButton.className = 'button add-to-cart-button';
    button.replaceWith(votingButton);

    // Sold-out products render a Klaviyo "Notify me when available" trigger in
    // the same button slot; the vote button owns this slot in voting mode.
    form.querySelector('.klaviyo-bis-trigger')?.remove();

    const voteStatus = this.#createVoteStatusIndicator();
    votingButton.insertAdjacentElement('afterend', voteStatus);

    this.#setVotingButtonLoading(votingButton, form, true);

    const productGid = 'gid://shopify/Product/' + productId;
    const productUrl = new URL(`${APP_PROXY_BASE}/community-votings/product`, window.location.origin);
    productUrl.searchParams.set('votingId', votingId);
    productUrl.searchParams.set('collectionHandle', collectionHandle);
    productUrl.searchParams.set('productId', productGid);

    const collectionUrl = new URL(`${APP_PROXY_BASE}/community-votings/collection`, window.location.origin);
    collectionUrl.searchParams.set('votingId', votingId);
    collectionUrl.searchParams.set('collectionHandle', collectionHandle);

    const voteUrl = new URL(`${APP_PROXY_BASE}/community-votings/vote`, window.location.origin);

    const fetchVotingState = () => fetchJSON(productUrl);
    const fetchCollectionVotingState = () => fetchJSON(collectionUrl);
    const submitVoteAction = (payload) => fetchJSON(voteUrl, {
      method: 'POST',
      body: JSON.stringify({ votingId, collectionHandle, ...payload }),
    });

    const getSelectedProductId = (voting) => {
      const selectedProduct = Array.isArray(voting?.products)
        ? voting.products.find((product) =>
          product.hasVoted === true ||
          product.isSelected === true ||
          product.selected === true ||
          product.voted === true
        )
        : null;

      return normalizeProductId(
        voting?.selectedProductId ??
          voting?.selectedProduct?.productId ??
          voting?.vote?.productId ??
          voting?.product?.selectedProductId ??
          selectedProduct?.productId ??
          (voting?.product?.hasVoted ? voting.product?.productId : null)
      );
    };

    const applyVotingState = (data) => {
      if (data?.voting?.collectionHandle !== collectionHandle) {
        return false;
      }

      const voting = data.voting;
      const currentProductId = normalizeProductId(productId);
      const selectedProductId = getSelectedProductId(voting);
      const currentHasVote =
        voting.product?.hasVoted === true ||
        (selectedProductId && currentProductId && selectedProductId === currentProductId);

      form.dataset.votingHasVote = currentHasVote ? 'true' : 'false';
      form.dataset.votingHasAnyVote = selectedProductId ? 'true' : 'false';
      form.dataset.votingCollectionHandle = collectionHandle;
      form.dataset.votingProductId = voting.product?.productId ? String(voting.product.productId) : productGid;
      form.dataset.votingSelectedProductId = selectedProductId || '';
      return true;
    };

    const refreshVotingState = async () => {
      const appliedProductState = applyVotingState(await fetchVotingState());

      if (!appliedProductState || form.dataset.votingHasAnyVote !== 'true') {
        applyVotingState(await fetchCollectionVotingState());
      }
    };

    const setLabel = () => {
      const hasVote = form.dataset.votingHasVote === 'true';
      const hasAnyVote = form.dataset.votingHasAnyVote === 'true';

      if (hasVote) {
        votingButton.textContent = this.dataset.labelRetractVote || '';
        votingButton.dataset.voteAction = 'retract';
        return;
      }

      votingButton.textContent = hasAnyVote ? this.dataset.labelChangeVote || '' : this.dataset.labelUpvote || '';
      votingButton.dataset.voteAction = hasAnyVote ? 'change' : 'vote';
    };

    try {
      await refreshVotingState();
      setLabel();
    } catch (error) {
      console.error('community-voting failed to initialize product voting button', error);
      setLabel();
    } finally {
      this.#setVotingButtonLoading(votingButton, form, false);
    }

    votingButton.addEventListener('click', async () => {
      if (votingButton.dataset.loading === 'true') return;

      const action = votingButton.dataset.voteAction;
      let statusState = 'success';
      this.#setVoteStatus(voteStatus, null);
      this.#setVotingButtonLoading(votingButton, form, true);

      try {
        if (action === 'retract') {
          await submitVoteAction({ retract: true });
        } else if (action === 'change') {
          await submitVoteAction({ retract: true });
          await submitVoteAction({ productId: productGid });
        } else {
          await submitVoteAction({ productId: productGid });
        }

        await refreshVotingState();
        setLabel();
      } catch (error) {
        statusState = 'error';
        console.error('community-voting vote action failed', error);
        try {
          await refreshVotingState();
        } catch (refreshError) {
          console.error('community-voting failed to refresh voting button', refreshError);
        }
        setLabel();
      } finally {
        this.#setVotingButtonLoading(votingButton, form, false);
        this.#setVoteStatus(voteStatus, statusState);
      }
    });
  }
}

if (!customElements.get('community-voting-controller')) {
  customElements.define('community-voting-controller', CommunityVotingController);
}

const style = document.createElement('style');
style.textContent = `
  community-voting-controller {
    --community-voting-overlay-background: rgb(0 0 0 / 0.28);
    --community-voting-progress-idle: rgb(234 179 8 / 0.75);
    --community-voting-progress-selected: rgb(34 197 94 / 0.75);
    --community-voting-label-color: #fff;
  }

  community-voting-controller .product-card__content.product-grid__card {
    gap: 16px;
  }

  community-voting-controller[data-voting-active] .klaviyo-bis-trigger {
    display: none !important;
  }

  @media screen and (max-width: 749px) {
    community-voting-controller .product-card__content.product-grid__card {
      gap: 12px;
    }
  }

  .community-voting-status {
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-height: 1.5rem;
    margin: 0.5rem auto 0;
    padding: 0.25rem 0.5rem;
    border-radius: 999px;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1;
  }

  .community-voting-status[hidden] {
    display: none;
  }

  .community-voting-status--success {
    color: #15803d;
    background: rgb(22 163 74 / 0.12);
  }

  .community-voting-status--error {
    color: #b91c1c;
    background: rgb(220 38 38 / 0.12);
  }

  .product-card-vote-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: stretch;
    pointer-events: none;
  }

  .product-card-vote-overlay__background,
  .product-card-vote-overlay__progress,
  .product-card-vote-overlay__label {
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }

  .product-card-vote-overlay__background {
    background: var(--community-voting-overlay-background);
  }

  .product-card-vote-overlay__progress {
    left: 0;
    right: 0;
    top: auto;
    bottom: 0;
    border-radius: 0;
    background: var(--community-voting-progress-idle);
  }

  .product-card-vote-overlay__progress--selected {
    background: var(--community-voting-progress-selected);
  }

  .product-card-vote-overlay__progress--idle {
    background: var(--community-voting-progress-idle);
  }

  .product-card-vote-overlay__label {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    font-size: clamp(24px, 3.5vw, 44px);
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--community-voting-label-color);
    text-align: center;
    pointer-events: none;
  }

  .product-card-vote-overlay__label span {
    line-height: 1;
    text-shadow: 0 0 8px rgb(0 0 0 / 0.85);
  }
`;
document.head.append(style);
